import { Fragment } from 'react';
import { NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { IProjectSourcesPeriod } from 'views/ProFormaTable/types';
import { useCustomScroll } from 'views/ProFormaTable/hooks/useCustomScroll';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import ProjectActualsSourceRow from './ProjectActualsSourceRow/ProjectActualsSourceRow';
import ProjectActualsSourceGrandTotalRow from './ProjectActualsSourceGrandTotalRow/ProjectActualsSourceGrandTotalRow';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';

export interface IProjectSourcesActualsTableProps {
    sources?: {
        monthly?: IProjectSourcesPeriod[];
        yearly?: IProjectSourcesPeriod[];
    };
}

const ProjectSourcesActualsTable = ({ sources }: IProjectSourcesActualsTableProps) => {
    const { tableRef, handleTableScroll } = useCustomScroll('sources-table', ['uses-table']);
    const { periodCadence } = useProFormaSocketContext();

    let sourcesPeriods: IProjectSourcesPeriod[] = [];
    if (sources) {
        if (periodCadence === Periods.ANNUALLY && sources.yearly && sources.yearly.length > 0) {
            sourcesPeriods = sources.yearly;
        } else if (sources.monthly && sources.monthly.length > 0) {
            sourcesPeriods = sources.monthly;
        }
    }

    const projectHasSources = Boolean(sourcesPeriods.length && sourcesPeriods[0]?.sources?.length);

    return (
        <div
            className="d-flex m-3 scrollable-container pb-1"
            style={{ maxHeight: 'none' }}
            ref={tableRef}
            onScroll={() => handleTableScroll('table')}
        >
            <NSTable
                className="NSTable--sticky-headers NSTable--sticky-headers--full-width
                    NSTable--sticky-headers--six-sticky-columns ProjectActuals__table"
                style={{ maxHeight: 'none' }}
            >
                <thead className="NSTable__thead">
                    <tr className="NSTable__thead__tr">
                        <th className="NSTable__thead__tr__th align-middle py-1">
                            Source name
                        </th>
                        <th className="NSTable__thead__tr__th align-middle py-1" />
                        <th className="NSTable__thead__tr__th align-middle py-1" />
                        <th className="NSTable__thead__tr__th align-middle">
                            Funding traunch
                        </th>
                        <th className="NSTable__thead__tr__th align-middle text-right">
                            Total souce amount
                        </th>
                        <th className="NSTable__thead__tr__th align-middle text-right">
                            Pro rata source share of traunch
                        </th>
                        <RenderIf isTrue={projectHasSources}>
                            {sourcesPeriods.map((period, periodIndex) => (
                                <th
                                    key={`${periodCadence}-${period.periodName}-${period.periodStartDate}`}
                                    className="NSTable__thead__tr__th align-middle
                                            NSTable__thead__tr__th--grey-bg NSTable__thead__tr__th--width-150 py-1"
                                >
                                    <div className="d-flex flex-column">
                                        <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right mb-0">
                                            {periodCadence === Periods.ANNUALLY
                                                ? `YEAR ${periodIndex}`
                                                : `MONTH ${periodIndex}`}
                                        </div>
                                        <div className="h6 text-uppercase text-right text-nowrap mt-1">
                                            {period.periodName}
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </RenderIf>

                        <RenderIf isTrue={!projectHasSources}>
                            <th
                                className="NSTable__thead__tr__th align-middle NSTable__thead__tr__th--opacity-3
                                            NSTable__thead__tr__th--grey-bg NSTable__thead__tr__th--width-150 py-1"
                            >
                                <div className="d-flex flex-column">
                                    <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right mb-0">
                                        {periodCadence === Periods.ANNUALLY ? 'YEAR #' : 'MONTH #'}
                                    </div>
                                    <div className="h6 text-uppercase text-right text-nowrap mt-1">
                                        {periodCadence === Periods.ANNUALLY ? '20XX' : 'MON YEAR'}
                                    </div>
                                </div>
                            </th>
                        </RenderIf>
                    </tr>
                </thead>

                <tbody className="NSTable__tbody" key={`${periodCadence}-tbody`}>
                    <RenderIf isTrue={projectHasSources}>
                        {sourcesPeriods[0]?.sources?.map(({ traunch, sources }, traunchIndex) => (
                            <Fragment key={`${periodCadence}-traunch-${traunch}-fragment`}>
                                {sources.map(source => {
                                    const sourceIndex = sources.indexOf(source);
                                    return (
                                        <ProjectActualsSourceRow
                                            key={`${periodCadence}-traunch-${traunch}-source-${source.id}-${source.name}`}
                                            sourcesPeriods={sourcesPeriods}
                                            traunchIndex={traunchIndex}
                                            sourceIndex={sourceIndex}
                                        />
                                    );
                                })}

                                {/* traunch subtotal row */}
                                <ProjectActualsSourceRow
                                    key={`${periodCadence}-traunch-${traunch}-subtotal`}
                                    sourcesPeriods={sourcesPeriods}
                                    traunchIndex={traunchIndex}
                                />
                            </Fragment>
                        ))}
                    </RenderIf>

                    <RenderIf isTrue={!projectHasSources}>
                        <tr className="NSTable__tbody__tr text-dark">
                            <td className="NSTable__tbody__tr__td align-middle" colSpan={6}>
                                <div className="text-center text-dark my-5">No existing project sources.</div>
                            </td>
                            <td className="NSTable__tbody__tr__td--opacity-3" />
                        </tr>
                    </RenderIf>
                </tbody>

                <tfoot className="NSTable__tfoot font-weight-bold">
                    <ProjectActualsSourceGrandTotalRow
                        sourcesPeriods={sourcesPeriods}
                        projectHasSources={projectHasSources}
                    />
                </tfoot>
            </NSTable>
        </div>
    );
};

export default ProjectSourcesActualsTable;
